import { TooltipBox } from '@loomhq/lens';
import React, { useCallback, useEffect, useState } from 'react';
import { useLayer } from 'react-laag';

import Waveform from './waveform';
import { useClipsState } from './clips/state';
import { Clip } from '../../../../../shared/types/clips';

import styles from './styles.module.less';
import ReorderingArrows from './reordering-arrows';

interface Props {
  clip: Clip;
  idx: number;
  currentTime: number;
  allowReordering: boolean;
  onClick: (
    event: React.MouseEvent,
    { secs, clipIdx }: { secs: number; clipIdx: number }
  ) => void;
  onReordering: (originalIndex: number, swapToRight: boolean) => void;
  recentlyAddedInfo: JSX.Element | false;
}

const ClipEditor = ({
  clip,
  idx,
  currentTime,
  allowReordering,
  onClick,
  onReordering,
  recentlyAddedInfo,
}: Props): JSX.Element => {
  const { waveform_url: waveformUrl } = clip;

  const [waveformData, setWaveformData] = useState([]);

  const clipStateCallback = useCallback(
    state => ({
      clipCount: state.clips.length,
      focusedClipIndex: state.focusedClipIndex,
      focusedPlayRangeIndex: state.focusedPlayRangeIndex,
      hoveredClipId: state.hoveredClipId,
      updatePlayRange: state.updatePlayRangeForClip(idx),
      updateHoveredClipId: state.updateHoveredClipId,
    }),
    [idx]
  );

  const {
    clipCount,
    focusedClipIndex,
    focusedPlayRangeIndex,
    hoveredClipId,
    updatePlayRange,
    updateHoveredClipId,
  } = useClipsState(clipStateCallback);

  const isFirst = idx === 0;
  const isLast = idx === clipCount - 1;

  const isFocusedClip = idx === focusedClipIndex;

  const getWaveformJSON = useCallback(async () => {
    // @ts-expect-error ignore due to enabling strict null checks
    const resp = await fetch(waveformUrl);

    if (!resp.ok) {
      throw new Error('Failed to fetch waveform json');
    }

    const data = await resp.json();

    setWaveformData(data);
  }, [waveformUrl]);

  useEffect(() => {
    if (waveformUrl) {
      getWaveformJSON();
    }
  }, [waveformUrl, getWaveformJSON]);

  const { layerProps, triggerProps, renderLayer } = useLayer({
    isOpen: Boolean(recentlyAddedInfo),
    placement: 'top-center',
    possiblePlacements: ['top-start', 'top-center', 'top-end'],
    triggerOffset: -10,
    auto: true,
    container: 'edit-tools',
    ResizeObserver,
  });

  const handleReorderingClick = (swapToRight: boolean): void => {
    onReordering?.(idx, swapToRight);
  };

  const onMouseMove = () => {
    if (hoveredClipId === clip.id) {
      return;
    }

    updateHoveredClipId(clip.id);
  };

  return (
    <>
      {recentlyAddedInfo &&
        renderLayer(
          <div {...layerProps} style={{ ...layerProps.style }}>
            <TooltipBox>{recentlyAddedInfo}</TooltipBox>
          </div>
        )}
      <div
        id={`clipeditor${idx}`}
        {...triggerProps}
        className={styles.clipEditor}
        onMouseMove={onMouseMove}
        onMouseLeave={() => updateHoveredClipId(null)}
      >
        <Waveform
          key={clip.id}
          clipId={clip.id}
          clipIdx={idx}
          peaks={waveformData}
          peakCount={1000}
          isFocusedClip={isFocusedClip}
          cursorWidth={2}
          cursorVerticalMargin={16}
          cursorPos={currentTime}
          duration={clip.duration || 1}
          normalize={true}
          sectionOutlineWidth={2}
          onClick={onClick}
          // @ts-expect-error ignore due to enabling strict null checks
          playRanges={clip.playRanges}
          selectedPlayRange={focusedPlayRangeIndex}
          updatePlayRange={updatePlayRange}
          waveformStatus={clip.waveform_generation}
          // @ts-expect-error ignore due to enabling strict null checks
          hasAudio={clip.video_properties.microphone_enabled}
        />
        {allowReordering && (
          <ReorderingArrows
            disableLeft={isFirst}
            disableRight={isLast}
            onMoveLeft={() => handleReorderingClick(false)}
            onMoveRight={() => handleReorderingClick(true)}
          />
        )}
      </div>
    </>
  );
};

export default ClipEditor;
